// Consistent client/admin work-order sorting. Pure functions; no Firestore reads or writes.
const collator = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });

export function jobDateMillis(value) {
  if (value == null || value === '') return null;
  try {
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value < 1e11 ? value * 1000 : value;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      // Local noon avoids date-only parsing as UTC midnight on a preceding day.
      const n = new Date(`${value}T12:00:00`).getTime();
      return Number.isFinite(n) ? n : null;
    }
    const n = new Date(value).getTime();
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

export function formatJobDate(value) {
  const stamp = jobDateMillis(value);
  return stamp == null ? 'Not recorded' : new Date(stamp).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'});
}

export function jobServiceId(job) {
  const text = String(job?.workOrderNumber || job?.orderNumber || job?.referenceNumber || '');
  return /^B0T\d{5}$/.test(text) ? text : 'Pending ID';
}

function sortValue(job, field, lookups) {
  switch (field) {
    case 'appointment': return { type:'date', value: jobDateMillis(job.schedule?.date || job.appointmentDate) };
    case 'createdAt': return { type:'date', value: jobDateMillis(job.createdAt) };
    case 'serviceId': return { type:'text', value: jobServiceId(job) === 'Pending ID' ? null : jobServiceId(job) };
    case 'service': return { type:'text', value: job.serviceType || job.title || null };
    case 'status': return { type:'text', value: job.status || null };
    case 'client': return { type:'text', value: lookups.clientName?.(job) || job.clientCompany || job.clientName || job.clientEmail || null };
    case 'contractor': return { type:'text', value: lookups.contractorName?.(job) || null };
    case 'total': return { type:'number', value: Number.isFinite(job.quote?.totalCents) ? job.quote.totalCents : Number.isFinite(job.totalPrice) ? job.totalPrice * 100 : null };
    default: return { type:'date', value: jobDateMillis(job.createdAt) };
  }
}

export function sortWorkOrders(jobs, field = 'createdAt', direction = 'desc', lookups = {}) {
  const sign = direction === 'asc' ? 1 : -1;
  return [...jobs].sort((a, b) => {
    const left = sortValue(a, field, lookups);
    const right = sortValue(b, field, lookups);
    // Unknown dates and unassigned contractors always come last regardless of direction.
    if (left.value == null && right.value != null) return 1;
    if (right.value == null && left.value != null) return -1;
    if (left.value != null && right.value != null) {
      const delta = left.type === 'text' ? collator.compare(String(left.value), String(right.value)) : Math.sign(left.value - right.value);
      if (delta) return sign * delta;
    }
    // Stable default tie-breaker: most recent request first.
    const aDate = jobDateMillis(a.createdAt) || 0;
    const bDate = jobDateMillis(b.createdAt) || 0;
    if (aDate !== bDate) return bDate - aDate;
    return collator.compare(String(a.id || ''), String(b.id || ''));
  });
}
